import React, { forwardRef, useContext, useMemo, useState } from 'react'
import { observer } from 'mobx-react'
import RootStore from '@App/stores/root-store'
import { StoresContext } from '@App/contexts'
import './index.scss'
import { Maybe } from '@Types'
import GroupedTable, {
    GroupedTableInfo,
} from '@Scenes/RightsholderScene/RightsholderOverclaim/OverclaimView/GroupedPublishersTable/GroupedTable'
import { ICONS } from '@Const/common'
import { TableRowData } from '@Components/ViewWork/Table'
import { getOverclaimUploadDocumentFor } from '@App/api/routes/overclaim'
import { openExternalHttp } from '@App/navigation/safe-navigation'
import { OverclaimUploadedDocument } from '@Entities/overclaim/overclaim-uploaded-document'
import { generateDocumentUploadTableCells } from '@App/helpers/overclaim/document-upload-table-cells'

interface GroupedDocumentUploadTableProps {
    onAddDescriptionToDocumentClicked: (document: OverclaimUploadedDocument) => void
    onValidation: (isValid: boolean) => void
}

const GroupedDocumentUploadTable = forwardRef<HTMLDivElement, GroupedDocumentUploadTableProps>(
    ({ onAddDescriptionToDocumentClicked }: GroupedDocumentUploadTableProps, ref) => {
        const { overclaimViewStore, rightsholderStore } = useContext<RootStore>(StoresContext)
        const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})

        const downloadDocumentClicked = async (overclaimId: number, memberUploadId: number) => {
            const rightsHolderId = rightsholderStore.currentRightsholderId
            if (rightsHolderId) {
                const response = await getOverclaimUploadDocumentFor(rightsHolderId, overclaimId, memberUploadId)
                openExternalHttp(response.data.downloadLink)
            }
        }

        const uploadedDocumentToTableRow = (
            uploadedDocument: OverclaimUploadedDocument,
            overclaimId: number,
            canEditDescription: boolean
        ): TableRowData => ({
            name: undefined,
            removeCellIdentifier: uploadedDocument.name,
            removable: false,
            cells: generateDocumentUploadTableCells({
                uploadedDocument: uploadedDocument,
                onAddDescriptionToDocumentClicked: onAddDescriptionToDocumentClicked,
                onDownloadDocumentClicked: (memberUploadId) => downloadDocumentClicked(overclaimId, memberUploadId),
                canEditDescription: canEditDescription,
                isExpanded: expandedRows[uploadedDocument.memberUploadId] || false,
                onExpandedClick: () => {
                    setExpandedRows((prev) => ({
                        ...prev,
                        [uploadedDocument.memberUploadId]: !prev[uploadedDocument.memberUploadId],
                    }))
                },
            }),
        })

        const publisherTableInfo = useMemo((): GroupedTableInfo[][] => {
            const originalPublishers = overclaimViewStore.overclaimViewItem?.originalPublishers ?? []
            const isAcceptedMember = (memberIpId: Maybe<number>) =>
                originalPublishers
                    .filter((publisher) => publisher.topMemberIpId === memberIpId)
                    .find((publisher) => publisher.memberStatus)?.memberStatus === 'ACCEPTED'
            const currentRightsholderId = rightsholderStore.currentRightsholderId
            const currentMemberIsAccepted = isAcceptedMember(currentRightsholderId)

            return Object.entries(overclaimViewStore.overclaimDocuments)
                .map(([topMemberIpId, overclaimUploadedDocuments]) => {
                    const memberIpId = Number.parseInt(topMemberIpId, 10)
                    const canViewDocuments =
                        memberIpId === currentRightsholderId ||
                        (currentMemberIsAccepted && isAcceptedMember(memberIpId))

                    if (!canViewDocuments) {
                        return []
                    }

                    const publisherName = originalPublishers.find(
                        (value) => value.topMemberIpId === memberIpId
                    )?.topMemberName

                    return [...overclaimUploadedDocuments]
                        .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime())
                        .map((doc) =>
                            uploadedDocumentToTableRow(
                                doc,
                                overclaimViewStore.overclaimViewItem?.overclaimHeaderId || 0,
                                memberIpId === currentRightsholderId
                            )
                        )
                        .map((row): GroupedTableInfo => ({
                            groupedBy: publisherName,
                            groupedValue: undefined,
                            headerRow: undefined,
                            rows: [row],
                        }))
                })
                .filter((tableInfo) => tableInfo.length > 0)
        }, [
            overclaimViewStore.overclaimDocuments,
            overclaimViewStore.overclaimViewItem,
            rightsholderStore.currentRightsholderId,
            expandedRows,
        ])

        return (
            <>
                {publisherTableInfo.map((publisher: GroupedTableInfo[], index: number) => (
                    <GroupedTable
                        key={index}
                        tableInfo={publisher}
                        icon={ICONS.account}
                        rowsAreValid={() => true}
                        ref={ref}
                    />
                ))}
            </>
        )
    }
)
GroupedDocumentUploadTable.displayName = 'GroupedDocumentUploadTable'
export default observer(GroupedDocumentUploadTable)
